import React from "react";
import { Dialog, Box, Typography, DialogContent, DialogActions, Button, CircularProgress, useMediaQuery, Divider, Tabs, Tab, Chip } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { useSettings } from "../../providers/settings-provider";
import theme from "../../util/theme/theme";
import { formatRecurrenceLabel } from "../../util/recurrence";
import { getRecurrenceColor, getProvisionTags } from "../../pages/provisions";
import { SlideUpTransition } from "./mobile/mobile-bottom-sheet";

interface RelatedTransactionsDetailProps {
    open: boolean;
    provisionTemplateId: number | null;
    description?: string;
    // Header detail line + tags - all optional since callers other than the
    // Provisions page (which already has the full row) may not have them handy.
    amount?: string | number;
    startDate?: string;
    endDate?: string;
    endDateType?: 'on' | 'after' | 'never';
    occurrenceCount?: number;
    autoProvisionMeta?: string;
    onClose: () => void;
}

interface PatternAnalysisTransaction {
    guid: string;
    date: string;
    description: string;
    amount: number;
    isSource: boolean;
}

interface PatternAnalysisSnapshot {
    interval?: string;
    medianDays?: number | null;
    matchedRange?: [number, number] | null;
    usedTolerancePercent?: number;
    transactions?: PatternAnalysisTransaction[];
}

// Three read-only views of a recurring/one-time provision template, scoped to
// the template rather than a single occurrence:
//   0. Occurrences (default tab) - every transaction this template has
//      generated, including ones since deleted (deleteFutureOccurrences/
//      deleteAllOccurrences soft-delete rather than destroy specifically so
//      they still show up here, clearly marked, rather than vanishing with
//      no trace).
//   1. Related Transactions - the actual real-transaction records linked to
//      any of this template's occurrences (resolved server-side from each
//      occurrence's linkedTransactionId - see getGeneratedTransactionsByTemplate,
//      provision-template-controller.js).
//   2. Pattern Analysis - the frozen "Provision This" snapshot (source
//      transaction + matches + detected interval/median/tolerance) recorded
//      at creation time, if this template was created that way - see
//      createProvisionTemplate (provision-template-controller.js).
const RelatedTransactionsDetail: React.FC<RelatedTransactionsDetailProps> = ({ open, provisionTemplateId, description, amount, startDate, endDate, endDateType, occurrenceCount, autoProvisionMeta, onClose }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const relatedRowVirtualizerInstanceRef = React.useRef<any>(null);
    const occurrencesRowVirtualizerInstanceRef = React.useRef<any>(null);
    const { formatDate, formatAmount } = useSettings();

    const [activeTab, setActiveTab] = React.useState<0 | 1 | 2>(0);
    const tabTitles = ['Occurrences', 'Related Transactions', 'Pattern Analysis'] as const;
    const [fetchTransactions, setFetchTransactions] = useApi();

    React.useEffect(() => {
        if (!open || !provisionTemplateId) return;
        setActiveTab(0);
        setFetchTransactions(buildApiRequestConfig(routes.provisionTemplate.fetchTransactionsById, [{ param: 'id', value: provisionTemplateId }]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, provisionTemplateId]);

    const linkedTransactions = fetchTransactions.data?.linkedTransactions || [];
    const occurrences = fetchTransactions.data?.data || [];
    // Occurrences are all provisions (Related Transactions, by contrast, are
    // all real) - color them the same way the Transactions/Provisions pages
    // color provision rows, so this modal doesn't look like a plain,
    // uncategorized transaction list. This endpoint only ever serves
    // recurring or one-time templates (see templateType's own comment,
    // provision-template-controller.js), so there's no invoice/payroll case
    // to handle here.
    const templateType: string | undefined = fetchTransactions.data?.templateType;
    const isOneTimeTemplate = templateType === 'one-time';
    const tags = React.useMemo(() => getProvisionTags({ autoProvisionMeta }), [autoProvisionMeta]);
    // Sits under an "End Date" column header (see the details box below) -
    // no "Ends"/"Ends after" prefix needed, the header already says that.
    const endLabel = endDateType === 'never'
        ? 'No end date'
        : endDateType === 'after'
            ? `After ${occurrenceCount ?? '?'} occurrence${occurrenceCount === 1 ? '' : 's'}`
            : endDate
                ? formatDate(new Date(endDate))
                : null;
    const occurrenceRowBg = isOneTimeTemplate ? 'var(--color-onetime-provision-bg)' : 'var(--color-recurring-provision-bg)';
    const occurrenceRowText = isOneTimeTemplate ? 'var(--color-onetime-provision-text)' : 'var(--color-recurring-provision-text)';
    const patternAnalysis: PatternAnalysisSnapshot | null = fetchTransactions.data?.patternAnalysisSnapshot || null;
    const sourceTransaction = patternAnalysis?.transactions?.find(t => t.isSource) || null;
    const matchedTransactions = patternAnalysis?.transactions?.filter(t => !t.isSource) || [];

    // Shared by both the "Related Transactions" and "Occurrences" tables -
    // takes an optional textColor so Occurrences can pass the provision
    // color explicitly per-cell (Related Transactions calls this with none,
    // for the app's normal default text color). A row-level sx.color on
    // muiTableBodyRowProps looks like it should cascade to these via plain
    // CSS inheritance, but transactions-table-virtual.tsx's own
    // getProvisionTextColor helper exists specifically because that isn't
    // reliable through MaterialReactTable's cell structure - every colored
    // cell needs its own explicit color, same as that file does.
    const buildBaseColumns = (textColor?: string): MRT_ColumnDef<any>[] => [
        {
            accessorKey: 'date',
            header: 'Date',
            size: 100,
            Cell: ({ cell }) => (
                <Typography fontSize={isMobile ? 12 : 14} sx={textColor ? { color: textColor } : undefined}>
                    {formatDate(new Date(cell.getValue<string>()))}
                </Typography>
            ),
        },
        {
            accessorKey: 'amount',
            header: 'Amount',
            size: 100,
            // Negative/positive amount color is its own convention, app-wide
            // (transactions-table-virtual.tsx's 'amount' cell) - independent
            // of, and always wins over, provision-vs-real row/text coloring -
            // deliberately ignores textColor.
            Cell: ({ cell }) => {
                const value = cell.getValue<number>();
                return (
                    <Typography
                        fontSize={isMobile ? 12 : 14}
                        sx={{ color: Number(value) <= 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)' }}
                    >
                        {formatAmount?.(value)}
                    </Typography>
                );
            },
        },
        {
            accessorKey: 'description',
            header: 'Description',
            size: 220,
            Cell: ({ cell }) => (
                <Typography fontSize={isMobile ? 12 : 14} sx={textColor ? { color: textColor } : undefined}>
                    {cell.getValue<string>()}
                </Typography>
            ),
        },
        {
            accessorKey: 'category',
            header: 'Category',
            size: 120,
            Cell: ({ row }: any) => (
                <Typography fontSize={isMobile ? 12 : 14} sx={textColor ? { color: textColor } : undefined}>
                    {row.original.category?.name || '-'}
                </Typography>
            ),
        },
        {
            accessorKey: 'bank',
            header: 'Bank',
            size: 120,
            Cell: ({ row }: any) => (
                <Typography fontSize={isMobile ? 12 : 14} sx={textColor ? { color: textColor } : undefined}>
                    {row.original.bankAccount?.accountName || 'No Bank Account'}
                </Typography>
            ),
        },
    ];

    const relatedColumns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => buildBaseColumns(),
        [formatDate, formatAmount, isMobile]
    );

    const occurrenceColumns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => [
            ...buildBaseColumns(occurrenceRowText),
            {
                id: 'status',
                header: 'Status',
                size: 90,
                Cell: ({ row }: any) => {
                    const deleted = row.original.isActive === false;
                    const linked = !!row.original.linkedTransactionId;
                    const label = deleted ? 'Deleted' : linked ? 'Linked' : 'Unlinked';
                    const color = deleted ? 'var(--color-negative-amount)' : linked ? 'var(--color-primary)' : 'var(--color-text-muted)';
                    return (
                        <Typography fontSize={isMobile ? 12 : 14} sx={{ color, fontWeight: deleted || linked ? 600 : 400 }}>
                            {label}
                        </Typography>
                    );
                },
            },
        ],
        [formatDate, formatAmount, isMobile, occurrenceRowText]
    );

    const analysisColumns = React.useMemo<MRT_ColumnDef<PatternAnalysisTransaction>[]>(
        () => [
            {
                accessorKey: 'date',
                header: 'Date',
                size: 100,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 12 : 14}>{formatDate(new Date(cell.getValue<string>()))}</Typography>
                ),
            },
            {
                accessorKey: 'description',
                header: 'Description',
                size: 220,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 12 : 14}>{cell.getValue<string>()}</Typography>
                ),
            },
            {
                accessorKey: 'amount',
                header: 'Amount',
                size: 100,
                Cell: ({ cell }) => {
                    const value = cell.getValue<number>();
                    return (
                        <Typography
                            fontSize={isMobile ? 12 : 14}
                            sx={{ color: Number(value) <= 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)' }}
                        >
                            {formatAmount?.(value)}
                        </Typography>
                    );
                },
            },
        ],
        [formatDate, formatAmount, isMobile]
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? false : "md"}
            fullWidth
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                    : { borderRadius: 3 }
            }}
        >
            <DialogContent sx={{ pt: 3, px: isMobile ? 2 : 3, pb: 0 }}>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 700 }}>{tabTitles[activeTab]}</Typography>
                    {templateType && (
                        <Chip
                            label={formatRecurrenceLabel(templateType)}
                            size="small"
                            sx={{ background: getRecurrenceColor(templateType), color: '#fff', fontWeight: 600, fontSize: 11, height: 20, letterSpacing: 0.5 }}
                        />
                    )}
                    {tags.map(tag => (
                        <Chip
                            key={tag.label}
                            label={tag.label}
                            size="small"
                            sx={{ background: tag.color, color: '#fff', fontWeight: 600, fontSize: 11, height: 20, letterSpacing: 0.5 }}
                        />
                    ))}
                </Box>
                {(description || amount !== undefined || startDate || endLabel) && (
                    <Box
                        display="flex"
                        width="100%"
                        flexDirection={isMobile ? 'column' : 'row'}
                        sx={{ border: '1px solid #d7d7d7', borderRadius: 1, mt: 1, mb: 1.5, overflow: 'hidden' }}
                    >
                        {[
                            description ? { label: 'Description', value: description, color: undefined, flex: 2 } : null,
                            amount !== undefined
                                ? { label: 'Amount', value: formatAmount?.(Number(amount)), color: Number(amount) <= 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)', flex: 1 }
                                : null,
                            startDate ? { label: 'Start Date', value: formatDate(new Date(startDate)), color: undefined, flex: 1 } : null,
                            endLabel ? { label: 'End Date', value: endLabel, color: undefined, flex: 1 } : null,
                        ].filter((field): field is { label: string; value: string; color: string | undefined; flex: number } => !!field).map((field, i, arr) => (
                            <Box
                                key={field.label}
                                flex={field.flex}
                                sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    borderRight: !isMobile && i < arr.length - 1 ? '1px solid #d7d7d7' : 'none',
                                    borderBottom: isMobile && i < arr.length - 1 ? '1px solid #d7d7d7' : 'none',
                                }}
                            >
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{field.label}</Typography>
                                <Typography sx={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: field.color || 'var(--color-default-text)' }}>{field.value}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 36 }}>
                    <Tab label="Occurrences" value={0} sx={{ minHeight: 36, py: 0.5, fontSize: 13, textTransform: 'none' }} />
                    <Tab label="Related Transactions" value={1} sx={{ minHeight: 36, py: 0.5, fontSize: 13, textTransform: 'none' }} />
                    <Tab label="Pattern Analysis" value={2} sx={{ minHeight: 36, py: 0.5, fontSize: 13, textTransform: 'none' }} />
                </Tabs>
            </DialogContent>
            <Divider />
            <DialogContent sx={{ py: 1 }}>
                {fetchTransactions.isLoading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                        <CircularProgress size={40} />
                        <Typography variant="h6" color="text.secondary">Loading...</Typography>
                    </Box>
                ) : activeTab === 1 ? (
                    linkedTransactions.length === 0 ? (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                            <Typography variant="h6" color="text.secondary">No related transactions found</Typography>
                        </Box>
                    ) : (
                        <MaterialReactTable
                            // Distinct key from the Occurrences table below -
                            // without it, switching tabs updates props on what
                            // React/MaterialReactTable treats as the SAME table
                            // instance (both sit at the same position in this
                            // ternary) rather than mounting a fresh one, and
                            // Occurrences' row-level provision background/text
                            // color (muiTableBodyRowProps below) was leaking
                            // into this table, which never sets that prop at
                            // all.
                            key="related"
                            columns={relatedColumns}
                            data={linkedTransactions}
                            enableRowVirtualization
                            enableColumnVirtualization
                            rowVirtualizerInstanceRef={relatedRowVirtualizerInstanceRef}
                            rowVirtualizerOptions={{ overscan: 10 }}
                            columnVirtualizerOptions={{ overscan: 2 }}
                            enablePagination={false}
                            enableTopToolbar={false}
                            enableBottomToolbar={false}
                            enableColumnActions={false}
                            initialState={{ density: 'compact', sorting: [{ id: 'date', desc: true }] }}
                            muiTableContainerProps={{ sx: { height: isMobile ? 200 : 350, overflowY: 'auto' } }}
                            muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                            muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                        />
                    )
                ) : activeTab === 0 ? (
                    occurrences.length === 0 ? (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                            <Typography variant="h6" color="text.secondary">No occurrences found</Typography>
                        </Box>
                    ) : (
                        <MaterialReactTable
                            key="occurrences"
                            columns={occurrenceColumns}
                            data={occurrences}
                            enableRowVirtualization
                            enableColumnVirtualization
                            rowVirtualizerInstanceRef={occurrencesRowVirtualizerInstanceRef}
                            rowVirtualizerOptions={{ overscan: 10 }}
                            columnVirtualizerOptions={{ overscan: 2 }}
                            enablePagination={false}
                            enableTopToolbar={false}
                            enableBottomToolbar={false}
                            enableColumnActions={false}
                            initialState={{ density: 'compact', sorting: [{ id: 'date', desc: true }] }}
                            muiTableContainerProps={{ sx: { height: isMobile ? 200 : 350, overflowY: 'auto' } }}
                            muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                            muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                            muiTableBodyRowProps={({ row }) => ({
                                sx: {
                                    backgroundColor: occurrenceRowBg,
                                    color: occurrenceRowText,
                                    opacity: row.original.isActive === false ? 0.5 : 1,
                                },
                            })}
                        />
                    )
                ) : !patternAnalysis ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={1} width="100%">
                        <Typography variant="h6" color="text.secondary">No pattern analysis recorded</Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', maxWidth: 320 }}>
                            This provision wasn't created via "Provision This", so there's no source/matching transaction analysis to show.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: isMobile ? 320 : 450, overflowY: 'auto' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Interval</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{patternAnalysis.interval || '-'}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Median Days Between Transactions</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{patternAnalysis.medianDays ?? '-'}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Matched Range (days)</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                    {patternAnalysis.matchedRange ? `${patternAnalysis.matchedRange[0]} - ${patternAnalysis.matchedRange[1]}` : '-'}
                                </Typography>
                            </Box>
                            {patternAnalysis.usedTolerancePercent != null && (
                                <Box>
                                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Tolerance Used</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{patternAnalysis.usedTolerancePercent}%</Typography>
                                </Box>
                            )}
                        </Box>
                        {sourceTransaction && (
                            <>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Source Transaction</Typography>
                                <MaterialReactTable
                                    key="pattern-source"
                                    columns={analysisColumns}
                                    data={[sourceTransaction]}
                                    enableTopToolbar={false}
                                    enableBottomToolbar={false}
                                    enablePagination={false}
                                    enableColumnActions={false}
                                    enableSorting={false}
                                    initialState={{ density: 'compact' }}
                                    muiTablePaperProps={{ sx: { boxShadow: 'none', mb: 2 } }}
                                    muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                                />
                            </>
                        )}
                        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Matching Transactions</Typography>
                        {matchedTransactions.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>No matches were recorded.</Typography>
                        ) : (
                            <MaterialReactTable
                                key="pattern-matches"
                                columns={analysisColumns}
                                data={matchedTransactions}
                                enableTopToolbar={false}
                                enableBottomToolbar={false}
                                enablePagination={false}
                                enableColumnActions={false}
                                initialState={{ density: 'compact', sorting: [{ id: 'date', desc: true }] }}
                                muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                                muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                            />
                        )}
                    </Box>
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ justifyContent: 'flex-end', px: isMobile ? 1 : 3, pb: isMobile ? 1 : 2 }}>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RelatedTransactionsDetail;
